// Fee Review — the B3 approve/waive queue for SYSTEM-assessed fees (late
// fee / NSF / default interest) at /portfolio-admin/fee-review. Rows arrive
// pending_review from the automatic assessment engines; approving releases
// the fee toward billing, waiving requires a reason. triggerDescription is
// the plain-words explanation of why the fee was assessed — rendered
// prominently so the reviewer never has to reverse-engineer the trigger.
// Reads/writes gate on Fees.Review (route + in-page defense-in-depth).

import { useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  Button,
  Card,
  EmptyState,
  GlossaryText,
  PageHeader,
  Skeleton,
  StatusBadge,
  Table,
  THead,
  TBody,
  TR,
  TH,
  TD,
  type StatusTone,
} from "@/design-system/components/ui";
import { Modal } from "@/components/ui/Modal";
import { useToast } from "@/components/ui/Toast";
import { extractErrorMessage } from "@/api/client";
import { formatCurrency, formatCalendarDate } from "@/design-system/lib/format";
import { cn } from "@/design-system/lib/utils";
import { useAuth } from "@/hooks/useAuth";
import {
  FEE_REVIEW_SOURCE_TYPES,
  type FeeReviewItemDto,
  type FeeReviewStatus,
} from "@/api/portfolioAdmin/loanFeesApi";
import {
  useApproveFee,
  useFeeReviewQueue,
  useWaiveFee,
} from "@/hooks/portfolioAdmin/useLoanFees";

const VIEW_PERMISSION = "Fees.Review";

const STATUS_TONE: Record<FeeReviewStatus, StatusTone> = {
  pending_review: "warn",
  approved: "ok",
  waived: "muted",
  billed: "info",
  collected: "ok",
};

const STATUS_LABEL: Record<FeeReviewStatus, string> = {
  pending_review: "Pending review",
  approved: "Approved",
  waived: "Waived",
  billed: "Billed",
  collected: "Collected",
};

const SOURCE_LABEL: Record<string, string> = Object.fromEntries(
  FEE_REVIEW_SOURCE_TYPES.map((s) => [s.value, s.label]),
);

export default function FeeReviewPage() {
  const { hasPermission } = useAuth();
  const toast = useToast();
  const [params, setParams] = useSearchParams();

  // Derive at parse time — absent means the pending queue; "all" is the
  // explicit "recently reviewed" sentinel (no status filter server-side).
  const rawStatus = params.get("status");
  const view: "pending" | "all" = rawStatus === "all" ? "all" : "pending";
  const status = view === "pending" ? "pending_review" : undefined;

  const query = useFeeReviewQueue({ status });
  const items = useMemo(() => query.data?.items ?? [], [query.data]);
  const pendingCount = query.data?.pendingCount ?? 0;

  const approve = useApproveFee();
  const waive = useWaiveFee();

  // Waive modal state — the row being waived + the required reason.
  const [waiveTarget, setWaiveTarget] = useState<FeeReviewItemDto | null>(null);
  const [waiveReason, setWaiveReason] = useState("");

  const setView = (next: "pending" | "all") => {
    const nextParams = new URLSearchParams(params);
    if (next === "all") nextParams.set("status", "all");
    else nextParams.delete("status");
    setParams(nextParams, { replace: true });
  };

  const onApprove = async (item: FeeReviewItemDto) => {
    try {
      await approve.mutateAsync({ feeId: item.id, loanId: item.loanId });
      toast.success("Fee approved");
    } catch (err) {
      toast.error(extractErrorMessage(err));
    }
  };

  const openWaive = (item: FeeReviewItemDto) => {
    setWaiveReason("");
    setWaiveTarget(item);
  };

  const confirmWaive = async () => {
    if (!waiveTarget || waiveReason.trim().length === 0) return;
    try {
      await waive.mutateAsync({
        feeId: waiveTarget.id,
        loanId: waiveTarget.loanId,
        reason: waiveReason.trim(),
      });
      toast.success("Fee waived");
      setWaiveTarget(null);
    } catch (err) {
      toast.error(extractErrorMessage(err));
    }
  };

  if (!hasPermission(VIEW_PERMISSION)) {
    return (
      <div className="p-space-5">
        <PageHeader eyebrow="Finance" title="Fee Review" />
        <EmptyState
          title="Not authorized"
          description="You don't have permission to review system-assessed fees."
        />
      </div>
    );
  }

  return (
    <div className="p-space-5">
      <PageHeader
        eyebrow="Finance"
        title="Fee Review"
        description="System-assessed fees (late fees, NSF fees, default interest) awaiting review. Approve to release a fee toward billing, or waive it with a reason — nothing bills until it's approved."
      />

      <div className="flex flex-wrap items-center gap-space-4 mb-space-4">
        <div
          className="inline-flex rounded-md border border-border-light overflow-hidden"
          role="group"
          aria-label="Queue filter"
        >
          <ViewChip
            active={view === "pending"}
            onClick={() => setView("pending")}
            testId="fee-review-view-pending"
          >
            Pending review
          </ViewChip>
          <ViewChip
            active={view === "all"}
            onClick={() => setView("all")}
            testId="fee-review-view-all"
          >
            All
          </ViewChip>
        </div>
        <Card className="p-space-3" data-testid="fee-review-pending-count">
          <span className="text-caption uppercase tracking-label text-gray-mid mr-space-2">
            Pending review
          </span>
          <span className="text-h3 font-display font-bold text-onyx tabular-nums">
            {pendingCount}
          </span>
        </Card>
      </div>

      {query.isLoading ? (
        <Skeleton className="h-48 w-full" />
      ) : query.isError ? (
        <p
          className="text-small text-status-error"
          role="alert"
          data-testid="fee-review-error"
        >
          Couldn't load the fee review queue. Try again.
        </p>
      ) : items.length === 0 ? (
        <EmptyState
          title={view === "pending" ? "Nothing to review" : "No fees yet"}
          description={
            view === "pending"
              ? "No system-assessed fees are waiting for review."
              : "No system-assessed fees have been recorded yet."
          }
          variant="noResults"
        />
      ) : (
        <Table>
          <THead>
            <TR>
              <TH>Loan</TH>
              <TH>Fee</TH>
              <TH className="text-right">Amount</TH>
              <TH>Period</TH>
              <TH>Why it was assessed</TH>
              <TH>Status</TH>
              <TH>
                <span className="sr-only">Actions</span>
              </TH>
            </TR>
          </THead>
          <TBody>
            {items.map((item) => (
              <TR key={item.id} data-testid={`fee-review-row-${item.id}`}>
                <TD>
                  <Link
                    to={`/portfolio-admin/loans/${item.loanId}?tab=fee-obligation`}
                    className="text-gold-shadow hover:underline"
                  >
                    {item.loanNumber ?? "View loan"}
                  </Link>
                </TD>
                <TD>
                  <div>
                    <GlossaryText text={item.feeType} />
                  </div>
                  <div className="text-caption text-gray-mid">
                    {SOURCE_LABEL[item.sourceType] ?? item.sourceType}
                  </div>
                </TD>
                <TD className="text-right tabular-nums">
                  {formatCurrency(item.amount)}
                </TD>
                <TD className="text-gray-mid whitespace-nowrap">
                  {item.periodStart
                    ? formatCalendarDate(item.periodStart)
                    : formatCalendarDate(item.incurredDate)}
                </TD>
                <TD className="max-w-reading">
                  <span className="text-onyx">
                    {item.triggerDescription ? (
                      <GlossaryText text={item.triggerDescription} />
                    ) : (
                      "—"
                    )}
                  </span>
                  {item.status === "waived" && item.waiveReason && (
                    <div className="text-caption text-gray-mid">
                      Waived: {item.waiveReason}
                    </div>
                  )}
                </TD>
                <TD>
                  <StatusBadge tone={STATUS_TONE[item.status] ?? "muted"}>
                    {STATUS_LABEL[item.status] ?? item.status}
                  </StatusBadge>
                </TD>
                <TD>
                  {item.status === "pending_review" && (
                    <div className="flex justify-end gap-space-2">
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={() => void onApprove(item)}
                        disabled={approve.isPending}
                        data-testid={`fee-review-approve-${item.id}`}
                      >
                        Approve
                      </Button>
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => openWaive(item)}
                        data-testid={`fee-review-waive-${item.id}`}
                      >
                        Waive
                      </Button>
                    </div>
                  )}
                </TD>
              </TR>
            ))}
          </TBody>
        </Table>
      )}

      <Modal
        open={waiveTarget !== null}
        onClose={() => setWaiveTarget(null)}
        title="Waive this fee"
        description={
          waiveTarget
            ? `${SOURCE_LABEL[waiveTarget.sourceType] ?? waiveTarget.feeType} of ${formatCurrency(waiveTarget.amount)} on ${waiveTarget.loanNumber ?? "this loan"}. The fee will not bill; the waiver is audited.`
            : undefined
        }
        footer={
          <>
            <Button variant="secondary" onClick={() => setWaiveTarget(null)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={() => void confirmWaive()}
              disabled={waiveReason.trim().length === 0}
              loading={waive.isPending}
              data-testid="fee-review-waive-confirm"
            >
              Waive fee
            </Button>
          </>
        }
      >
        <label
          htmlFor="fee-waive-reason"
          className="font-display text-caption uppercase tracking-label text-gray-mid"
        >
          Reason for waiving (required)
        </label>
        <textarea
          id="fee-waive-reason"
          className="mt-1 w-full min-h-[88px] border border-border-light rounded-md px-space-2 py-space-1 text-small bg-white-pure"
          value={waiveReason}
          onChange={(e) => setWaiveReason(e.target.value)}
          data-testid="fee-review-waive-reason"
        />
      </Modal>
    </div>
  );
}

function ViewChip({
  active,
  onClick,
  testId,
  children,
}: {
  active: boolean;
  onClick: () => void;
  testId: string;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={cn(
        "px-space-3 py-space-1 text-small transition-colors duration-fast",
        active
          ? "bg-onyx text-white-pure font-semibold"
          : "bg-white-pure text-onyx hover:bg-white-warm",
      )}
      data-testid={testId}
    >
      {children}
    </button>
  );
}
